import { useEffect, useState, type FormEvent } from "react";
import { financeApi, propertyApi, type IncomeDto, type ExpenseDto, type FinancialSummaryDto, type PropertyDto } from "../api.js";
import { Button, Card, EmptyState, ErrorBanner, Field, MetricCard, Money, PageHeader, Select, Spinner, TextInput } from "../components/ui.js";

export function FinancePage() {
  const [summary, setSummary] = useState<FinancialSummaryDto | null>(null);
  const [income, setIncome] = useState<IncomeDto[] | null>(null);
  const [expenses, setExpenses] = useState<ExpenseDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const [s, i, e] = await Promise.all([financeApi.summary(), financeApi.listIncome(), financeApi.listExpenses()]);
    if (s.ok && s.data) setSummary(s.data); else setError("Failed to load summary");
    setIncome(i.ok ? i.data ?? [] : []);
    setExpenses(e.ok ? e.data ?? [] : []);
  };
  useEffect(() => { void load(); }, []);

  if (error) return <ErrorBanner message={error} />;
  if (!summary) return <Spinner />;

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Money"
        title="Finance"
        description="Record money in and out, then see the current position across your portfolio."
      />
      <div className="metric-grid">
        <MetricCard label="Total income" value={<Money amount={summary.totalIncome} currency={summary.currency} />} hint={`${summary.incomeCount} recorded item${summary.incomeCount === 1 ? "" : "s"}`} tone="positive" />
        <MetricCard label="Total expenses" value={<Money amount={summary.totalExpenses} currency={summary.currency} />} hint={`${summary.expenseCount} recorded item${summary.expenseCount === 1 ? "" : "s"}`} />
        <MetricCard label="Net position" value={<Money amount={summary.netPosition} currency={summary.currency} />} hint="Income less recorded expenses" tone={summary.netPosition >= 0 ? "positive" : "attention"} />
      </div>
      <div className="page-grid-2">
        <Card title="Income" description="Add and review income received for your properties.">
          <AddIncomeForm onSaved={load} />
          {income === null ? <Spinner /> : income.length === 0 ? <EmptyState title="No income recorded" message="Choose a property above to record your first income item." /> : (
            <div className="table-shell"><table className="data-table"><thead><tr><th>Date</th><th>Amount</th><th>Source</th></tr></thead>
              <tbody>{income.map((x) => <tr key={x.id}><td>{x.occurredOn.slice(0, 10)}</td><td><Money amount={x.amount} currency={x.currency} /></td><td>{x.source}</td></tr>)}</tbody>
            </table></div>
          )}
        </Card>
        <Card title="Expenses" description="Capture the costs that affect what you keep.">
          <AddExpenseForm onSaved={load} />
          {expenses === null ? <Spinner /> : expenses.length === 0 ? <EmptyState title="No expenses recorded" message="Choose a property above to record your first expense." /> : (
            <div className="table-shell"><table className="data-table"><thead><tr><th>Date</th><th>Amount</th><th>Category</th></tr></thead>
              <tbody>{expenses.map((x) => <tr key={x.id}><td>{x.incurredOn.slice(0, 10)}</td><td><Money amount={x.amount} currency={x.currency} /></td><td>{x.category}</td></tr>)}</tbody>
            </table></div>
          )}
        </Card>
      </div>
    </div>
  );
}

function AddIncomeForm({ onSaved }: { onSaved: () => void }) {
  const [props, setProps] = useState<PropertyDto[]>([]);
  const [form, setForm] = useState({ propertyId: "", amount: "", source: "rent", occurredOn: new Date().toISOString().slice(0, 10) });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { void propertyApi.list().then((r) => setProps(r.data ?? [])); }, []);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault(); setError(null); setBusy(true);
    const res = await financeApi.recordIncome({ propertyId: form.propertyId, amount: Number(form.amount), currency: "NZD", source: form.source, occurredOn: form.occurredOn, origin: 0 });
    setBusy(false);
    if (!res.ok) { setError(res.error?.errorCode ?? "Failed"); return; }
    setForm({ ...form, amount: "" }); onSaved();
  };
  return (
    <form onSubmit={onSubmit} className="form-inline">
      {error && <ErrorBanner message={error} />}
      <Field label="Property"><Select required value={form.propertyId} onChange={(e) => setForm({ ...form, propertyId: e.target.value })}>
        <option value="">Select property…</option>
        {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </Select></Field>
      <Field label="Amount"><TextInput type="number" min="0" step="0.01" placeholder="0.00" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
      <Field label="Source"><TextInput placeholder="For example, rent" required value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></Field>
      <Field label="Date"><TextInput type="date" required value={form.occurredOn} onChange={(e) => setForm({ ...form, occurredOn: e.target.value })} /></Field>
      <Button type="submit" disabled={busy}>{busy ? "Adding…" : "Add income"}</Button>
    </form>
  );
}

function AddExpenseForm({ onSaved }: { onSaved: () => void }) {
  const [props, setProps] = useState<PropertyDto[]>([]);
  const [form, setForm] = useState({ propertyId: "", amount: "", category: "maintenance", incurredOn: new Date().toISOString().slice(0, 10) });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { void propertyApi.list().then((r) => setProps(r.data ?? [])); }, []);
  const onSubmit = async (e: FormEvent) => {
    e.preventDefault(); setError(null); setBusy(true);
    const res = await financeApi.recordExpense({ propertyId: form.propertyId, amount: Number(form.amount), currency: "NZD", category: form.category, incurredOn: form.incurredOn, origin: 0 });
    setBusy(false);
    if (!res.ok) { setError(res.error?.errorCode ?? "Failed"); return; }
    setForm({ ...form, amount: "" }); onSaved();
  };
  return (
    <form onSubmit={onSubmit} className="form-inline">
      {error && <ErrorBanner message={error} />}
      <Field label="Property"><Select required value={form.propertyId} onChange={(e) => setForm({ ...form, propertyId: e.target.value })}>
        <option value="">Select property…</option>
        {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </Select></Field>
      <Field label="Amount"><TextInput type="number" min="0" step="0.01" placeholder="0.00" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
      <Field label="Category"><TextInput placeholder="For example, maintenance" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
      <Field label="Date"><TextInput type="date" required value={form.incurredOn} onChange={(e) => setForm({ ...form, incurredOn: e.target.value })} /></Field>
      <Button type="submit" disabled={busy}>{busy ? "Adding…" : "Add expense"}</Button>
    </form>
  );
}
